/**
 * Чистая логика настройки Sentry для `src/index.tsx` — без самого SDK, чтобы
 * её можно было проверить тестом.
 *
 * Почему отдельно. Раньше `index.tsx` читал `process.env.SENTRY_ENVIRONMENT` и
 * `process.env.SENTRY_DSN`, но выброшенный (ejected) CRA вшивает в сборку
 * только `NODE_ENV` и `REACT_APP_*` (`config/env.js`). Обе переменные всегда
 * были `undefined`: прод сообщал о себе как `development`, а дев-серверы слотов
 * на localhost слали ошибки в тот же проект Sentry, что и прод.
 */

/** Переменные окружения, от которых зависит настройка (все — из сборки). */
export interface ISentryEnv {
  NODE_ENV?: string;
  REACT_APP_THEME?: string;
  REACT_APP_SENTRY_DSN?: string;
  REACT_APP_SENTRY_ENVIRONMENT?: string;
  REACT_APP_SENTRY_RELEASE?: string;
}

/** То, что уходит в `Sentry.init` помимо интеграций и частоты трассировки. */
export interface ISentryConfig {
  dsn: string;
  environment: string;
  enabled: boolean;
  release?: string;
  ignoreErrors: string[];
}

/** Проект Sentry по умолчанию (ветка `REACT_APP_THEME !== 'HACKERU'`). */
export const DEFAULT_SENTRY_DSN =
  'https://96cc8251550145c6a919b90098669dc2@o1081788.ingest.sentry.io/6168870';

/** Проект Sentry для темы HACKERU (EU, орг `learn-it`). */
export const HACKERU_SENTRY_DSN =
  'https://e5416de83211b48c8a1af6d88ee86e04@o4507724167249920.ingest.de.sentry.io/4507724170592336';

/**
 * Сетевой шум браузера: обрыв связи, офлайн, закрытая вкладка посреди запроса.
 * Это не дефект кода — сам сбой уже показан пользователю через error link
 * Apollo. Тексты по браузерам: Chrome, Firefox, Safari.
 */
export const SENTRY_IGNORE_ERRORS = [
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed',
];

const trimmed = (value?: string): string => (value || '').trim();

/** Локальная машина: localhost, 127.0.0.1, [::1] и любые `*.localhost`. */
export const isLocalHostname = (hostname: string): boolean => {
  const host = trimmed(hostname).toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host === '[::1]' ||
    host.endsWith('.localhost')
  );
};

/**
 * Боевые домены ERP. Сборка stage/uat — тоже `NODE_ENV=production`, а пайплайн
 * не передаёт `REACT_APP_SENTRY_ENVIRONMENT`; без этого списка их ошибки легли
 * бы в окружение `production` и будили бы боевые алерты.
 */
export const PRODUCTION_HOSTNAMES = ['erp.learnit.com.pl'];

/**
 * Явное окружение из сборки. Иначе боевая сборка на боевом домене —
 * `production`, на любом другом домене — сам домен (stage/uat видны отдельно),
 * не боевая сборка — `development`.
 */
export const sentryEnvironment = (env: ISentryEnv, hostname = ''): string => {
  const explicit = trimmed(env.REACT_APP_SENTRY_ENVIRONMENT);
  if (explicit) return explicit;
  if (env.NODE_ENV !== 'production') return 'development';
  const host = trimmed(hostname).toLowerCase();
  if (!host || PRODUCTION_HOSTNAMES.includes(host)) return 'production';
  return host;
};

/** Шлём только из боевой сборки и только не с локальной машины. */
export const sentryEnabled = (env: ISentryEnv, hostname: string): boolean =>
  env.NODE_ENV === 'production' && !isLocalHostname(hostname);

/** Релиз — только если сборка его задала; иначе `undefined`. */
export const sentryRelease = (env: ISentryEnv): string | undefined =>
  trimmed(env.REACT_APP_SENTRY_RELEASE) || undefined;

/** DSN из сборки, иначе прежний выбор по теме. */
export const sentryDsn = (env: ISentryEnv): string =>
  trimmed(env.REACT_APP_SENTRY_DSN) ||
  (env.REACT_APP_THEME === 'HACKERU' ? HACKERU_SENTRY_DSN : DEFAULT_SENTRY_DSN);

export const buildSentryConfig = (
  env: ISentryEnv,
  hostname: string,
): ISentryConfig => ({
  dsn: sentryDsn(env),
  environment: sentryEnvironment(env, hostname),
  enabled: sentryEnabled(env, hostname),
  release: sentryRelease(env),
  ignoreErrors: [...SENTRY_IGNORE_ERRORS],
});
